import { useState, useEffect, FormEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Input from '@gym-atoms/Input/Input';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import { useForm } from 'react-hook-form';
import { AlertType, createAlert } from '@gym-atoms/Alert/Alert';
import {
  GymChainAccessCategoryInput,
  SiteLocationAccessCategorySite,
  LambdaResponse,
  AddSiteLocationAccessCategoryInput,
  updateAccessCategoryGroupIdInput,
  AccessCategoryMembershipInfo
} from '@gym-src/API';
import { API } from 'aws-amplify';
import {
  addAccessCategory,
  addGymChainAccessCategory,
  modifyGymChainAccessCategory,
  removeSiteLocationAccessCategory,
  updateAccessCategoryGroupId
} from '@gym-graphql/mutations';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Text from '@gym-atoms/Text/Text';
import { GymChainAccessCategory, partialGymType } from '@gym-particles/types/models';
import { UserRole } from '@gym-particles/types/User';
import { getPartialGymsOfGymChain } from '@gym-particles/queries';
import Loader from '@gym-atoms/Loader/Loader';
import {
  getAllMembershipsForAccessCategories,
  getSiteLocationAccessCategories
} from '@gym-graphql/queries';
import { fetchGymChainAccessCategory } from '../../../redux/slices/gymChainAccessCategorySlice';
import Checkbox from '../../atoms/Checkbox/Checkbox';

const GymChainAccessCategoryInfoTemplate = (props: GymChainAccessCategoryInfoTemplateProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { gymChainAccessCategoryId, chainId } = useParams<{
    gymChainAccessCategoryId: string;
    chainId: string;
  }>();
  const dispatch = useAppDispatch();
  const [failedDialogVisible, setFailedDialogVisible] = useState(false);
  const [gymsDropDownItems, setGymsDropDownItems] = useState<Array<SelectItem>>([]);
  const [membershipsDropDownItems, setMembershipsDropDownItems] = useState<Array<SelectItem>>([]);
  const [isMembershipLoading, setIsMembershipLoading] = useState(false);
  const isAccessCategoriesLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [defaultSelectedGyms, setDefaultSelectedGyms] = useState<
    Array<SiteLocationAccessCategorySite>
  >([]);
  const [defaultSelectedMemberships, setDefaultSelectedMemberships] = useState([]);
  const [gymRemovableCheckLoading, setGymRemovableCheckLoading] = useState(false);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const userId = useAppSelector((state) => state.user.userId);
  const userRole = useAppSelector((state) => state.user.userRole);
  const [isInserted, setIsInserted] = useState(false);
  const [schedulesOnly, setSchedulesOnly] = useState(true);
  const [filterMemberships, setFilterMemberships] = useState(false);
  const [sortField, setSortField] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState(-1);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [searchTerm, setSearchTerm] = useState('');

  const getTranslationKey = (e: string): string => {
    return props.type === 'new' ? 'ADD_NEW_ACCESS_CATEGORY.' + e : 'MODIFY_ACCESS_CATEGORY.' + e;
  };

  type FormInputs = {
    id?: number;
    siteId?: number;
    name?: string;
    gender?: string;
    age?: number;
    schedulesOnly?: string;
    status?: string;
    gyms?: Array<string>;
    memberships?: Array<number>;
    groupId?: string;
  };

  const defaultValues: FormInputs = {
    id: props.accessCategory?.id || 0,
    siteId: props.accessCategory?.siteId || 0,
    name: props.accessCategory?.name || '',
    gender: props.accessCategory?.gender || '',
    age: props.accessCategory?.age || 0,
    schedulesOnly: props.accessCategory?.schedulesOnly ? 'true' : 'false',
    status: props.accessCategory?.status || '',
    gyms: [],
    memberships: [],
    groupId: props.accessCategory?.groupId || ''
  };

  const genderDropDownItems = [
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_MALE')),
      value: 'male'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_FEMALE')),
      value: 'female'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_NA')),
      value: 'n/a'
    }
  ];

  const inputFieldChangeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof GymChainAccessCategoryInput, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
  };

  const [formValues, setFormValues] = useState(defaultValues);

  const [tempGymSelectionValues, setTempGymSelectionValues] = useState<string[] | undefined>(
    formValues.gyms
  );
  const [tempMembershipSelectVal, setTempMembershipSelectVal] = useState<number[] | undefined>(
    formValues.memberships
  );

  const { register, handleSubmit, setValue, errors } = useForm<FormInputs>({
    defaultValues: defaultValues
  });

  const DeleteDialogFooter = () => {
    return (
      <div>
        {gymRemovableCheckLoading ? (
          <></>
        ) : (
          <Button
            label={t('COMMON.OK')}
            variant={ButtonVariants.basic}
            onClick={() => setIsDeleteDialogVisible(false)}
          />
        )}
      </div>
    );
  };
  const RemovableGymDialog = () => {
    return (
      <div>
        <DialogBox
          dialogVisible={isDeleteDialogVisible}
          variant={DialogBoxVariants.long}
          dialogDismissableMask={true}
          onHideCallback={() => setIsDeleteDialogVisible(false)}
          dialogFooter={<DeleteDialogFooter />}
          dialogClosable={false}
          dialogHeader={
            gymRemovableCheckLoading
              ? t('MEMBERSHIP_LIST.TABLE.CHECK_LINKED_GYMS')
              : t('MEMBERSHIP_LIST.TABLE.DELETE_MODAL_HEADER')
          }
          content={
            gymRemovableCheckLoading ? (
              <>
                <div className="mb-3">
                  <Loader shape="rectangle" width="full" />
                </div>
                <div className="mb-3">
                  <Loader shape="rectangle" width="full" />
                </div>
                <div className="mb-3">
                  <Loader shape="rectangle" width="half" />
                </div>
              </>
            ) : (
              <Text>{t('MEMBERSHIP_LIST.TABLE.DELETE_GYM_MODAL_CANNOT')}</Text>
            )
          }
        />
      </div>
    );
  };

  const checkRemovableMemberships = async (gyms: number[]) => {
    console.log('checking removable memberships');
    return true;
  };

  const checkRemovableGyms = async (gyms: number[]) => {
    console.log({ gyms });
    const updatedData = defaultSelectedMemberships
      .map((membership) => ({
        ...membership,
        siteLocations: membership.siteLocations.filter((loc) => gyms.includes(loc.siteLocationId))
      }))
      .filter((membership) => membership.siteLocations.length > 0);
    console.log({ updatedData });
    return updatedData.map((m) => m.membershipId);
  };

  const getGyms = () => {
    try {
      const response = (API.graphql({
        query: getPartialGymsOfGymChain,
        variables: {
          id: chainId,
          offset: 0,
          pageSize: 1000,
          sortField: 'gymName',
          sortOrder: 'ASC',
          userId: userRole === UserRole.SYSTEM_ADMIN ? 0 : userId
        }
      }) as Promise<{
        data: { getGymsOfGymChain: { items: partialGymType[] } };
      }>).then((e) => {
        const gymsDropDown: SelectItem[] = e.data.getGymsOfGymChain.items
          .filter((gym) => gym.gymId)
          .map((gym) => {
            return { label: gym.gymName, value: String(gym.gymId) };
          });

        setGymsDropDownItems(gymsDropDown);
        props.type === 'modify' && getDefaultSelectedGyms();
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const getGymMemberships = async () => {
    setIsMembershipLoading(true);

    try {
      const variables = {
        siteLocationId: `(${formValues.gyms.join(', ')})`,
        filterMemberships: filterMemberships,
        accessCategoryId: props.type === 'modify' ? props.accessCategory.id : 0
      };

      const response = await (API.graphql({
        query: getAllMembershipsForAccessCategories,
        variables
      }) as Promise<{
        data: { getAllMembershipsForAccessCategories: AccessCategoryMembershipInfo[] };
      }>);

      const fetchedItems = response.data.getAllMembershipsForAccessCategories || [];
      console.log({ fetchedItems });
      const dropDownItems = Array.from(
        new Map(
          fetchedItems
            .filter((data) => data && data.membershipId != null)
            .map((data) => ({
              label: `${data.membershipName} (${data.importedId}) • ${data.program} [${data.scheduleType}]`,
              value: data.membershipId
            }))
            .map((item) => [item.value, item])
        ).values()
      );
      console.log({ dropDownItems });

      setMembershipsDropDownItems(dropDownItems);
    } catch (error) {
      console.error('Error fetching memberships:', error);
      setFailedDialogVisible(true);
    } finally {
      setIsMembershipLoading(false);
    }
  };

  const getDefaultSelectedGyms = async () => {
    console.log('defaultSelectedGyms', gymChainAccessCategoryId);
    try {
      const defaultGymsResponse = await (API.graphql({
        query: getSiteLocationAccessCategories,
        variables: {
          accessCategoryId: +gymChainAccessCategoryId
        }
      }) as Promise<{
        data: { getSiteLocationAccessCategories: SiteLocationAccessCategorySite[] };
      }>);
      console.log({ fetchedGyms: defaultGymsResponse.data });

      setDefaultSelectedGyms(
        [
          ...new Set(
            defaultGymsResponse.data.getSiteLocationAccessCategories.map(
              (item) => item.siteLocationId
            )
          )
        ].map((ts) =>
          defaultGymsResponse.data.getSiteLocationAccessCategories.find(
            (item) => item.siteLocationId === ts
          )
        )
      );
      const groupedMembershipData = Object.values(
        defaultGymsResponse.data.getSiteLocationAccessCategories.reduce((acc, current) => {
          const {
            membershipId,
            membershipImportedId,
            membershipName,
            ...siteLocationDetails
          } = current;

          if (!acc[membershipId]) {
            acc[membershipId] = {
              membershipId,
              membershipImportedId,
              membershipName,
              siteLocations: []
            };
          }

          acc[membershipId].siteLocations.push(siteLocationDetails);

          return acc;
        }, {})
      );
      setDefaultSelectedMemberships(groupedMembershipData);
    } catch (error) {
      console.error('Failed to fetch selected gyms', error);
    }
  };

  useEffect(() => {
    getGyms();
    dispatch(
      fetchGymChainAccessCategory({
        siteId: props.accessCategory?.siteId || 0
      })
    );
  }, []);

  useEffect(() => {
    if (formValues.gyms && formValues.gyms.length > 0) {
      getGymMemberships();
    } else {
      setMembershipsDropDownItems([]);
    }
  }, [formValues.gyms, filterMemberships]);

  const gymsDropdownHandler = async (e: DropdownChangeEvent) => {
    setFormValues({ ...formValues, gyms: e.value });
    const newGymSelection = e.value as string[];
    const currentGyms = formValues.gyms || [];

    const removedGyms = currentGyms.filter((id) => !newGymSelection.includes(id));
    const addedGyms = newGymSelection.filter((id) => !currentGyms.includes(id));

    let updatedMemberships = [...formValues.memberships];

    if (removedGyms.length > 0) {
      const remainingIds = await checkRemovableGyms(newGymSelection.map((id) => +id));
      console.log({ remainingIds });
      if (remainingIds) {
        updatedMemberships = remainingIds.map((id) => Number(id));
      }
    }

    console.log({ addedGyms });
    if (addedGyms.length > 0) {
      addedGyms.forEach((gymId) => {
        const membershipsForGym = defaultSelectedMemberships
          .filter((m) => m.siteLocations.some((loc) => loc.siteLocationId === +gymId))
          .map((m) => m.membershipId);

        console.log({ membershipsForGym });
        updatedMemberships = Array.from(new Set([...updatedMemberships, ...membershipsForGym]));
      });
    }

    setValue('gyms', newGymSelection);
    setValue('memberships', updatedMemberships);
    console.log({ updatedMemberships });

    setFormValues({
      ...formValues,
      gyms: newGymSelection,
      memberships: updatedMemberships
    });

    setTempGymSelectionValues(newGymSelection);
  };

  const membershipsDropdownHandler = async (e: DropdownChangeEvent) => {
    const rawValues = e.value as any[];
    const selectedIds = rawValues
      .filter((val) => val != null && val !== '')
      .map((val) => Number(val));

    const removedMemberships = defaultSelectedMemberships
      .map((y: SiteLocationAccessCategorySite) => y.membershipId)
      .filter((x: number) => !e.value.includes(String(x)));

    let response;

    if (removedMemberships.length > 0) {
      if (tempMembershipSelectVal && tempMembershipSelectVal?.length > e.value.length) {
        response = await checkRemovableMemberships(removedMemberships);
      } else {
        response = true;
      }
    } else {
      response = true;
    }
    if (response) {
      setValue('memberships', selectedIds);
      setFormValues({ ...formValues, memberships: selectedIds });
    } else {
      return;
    }
    setTempMembershipSelectVal(e.value);
  };

  useEffect(() => {
    setFormValues({
      ...formValues,
      id: props.accessCategory?.id || 0,
      siteId: props.accessCategory?.siteId || 0,
      name: props.accessCategory?.name || '',
      gender: props.accessCategory?.gender || '',
      schedulesOnly: props.accessCategory?.schedulesOnly ? 'true' : 'false',
      status: props.accessCategory?.status || '',
      gyms: (defaultSelectedGyms || []).map((gym) => String(gym.siteLocationId)),
      memberships: (defaultSelectedMemberships || []).map((membership) =>
        Number(membership.membershipId)
      )
    });

    console.log({ defaultSelectedGyms });
    console.log({ defaultSelectedMemberships });

    setValue('name', props.accessCategory?.name);
    setValue('siteId', props.accessCategory?.siteId);
    setValue('gender', props.accessCategory?.gender);
    setValue('age', props.accessCategory?.age);
    setValue('schedulesOnly', props.accessCategory?.schedulesOnly);
    setValue('status', props.accessCategory?.status);
    setValue(
      'gyms',
      defaultSelectedGyms.map((gym) => String(gym.siteLocationId))
    );
    setValue(
      'memberships',
      defaultSelectedMemberships.map((membership) => Number(membership.membershipId))
    );

    setTempGymSelectionValues(defaultSelectedGyms.map((gym) => String(gym.siteLocationId)));
    setTempMembershipSelectVal(
      defaultSelectedMemberships.map((membership) => Number(membership.membershipId))
    );
  }, [props, defaultSelectedGyms, defaultSelectedMemberships]);

  const ActionFailedDialog = () => {
    return (
      <div>
        <DialogBox
          variant={DialogBoxVariants.basic}
          dialogVisible={failedDialogVisible}
          onHideCallback={() => setFailedDialogVisible(false)}
          dialogHeader={t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER'))}
          dialogFooter={
            <Button
              label={t(getTranslationKey('FORM_FAILURE_DIALOG_BUTTON'))}
              onClick={() => setFailedDialogVisible(false)}
            />
          }
          dialogDismissableMask={true}
          dialogClosable={false}
          content={<Text>{t(getTranslationKey('FORM_FAILURE_DIALOG_BODY'))}</Text>}
        />
      </div>
    );
  };

  const removeLocationAccessCategory = async (accessCategoryId: number) => {
    try {
      console.log(accessCategoryId);
      await API.graphql({
        query: removeSiteLocationAccessCategory,
        variables: {
          id: accessCategoryId
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const modifySiteLocationAccessCategory = async (
    modifySiteLocationAccessCategoryInput: updateAccessCategoryGroupIdInput
  ) => {
    console.log({ modifySiteLocationAccessCategoryInput });
    try {
      await API.graphql({
        query: updateAccessCategoryGroupId,
        variables: {
          input: modifySiteLocationAccessCategoryInput
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addSiteLocationAccessCategory = async (
    data: FormInputs,
    addAccessCategoryResponse: GymChainAccessCategory
  ) => {
    try {
      console.log({ memberships: data.memberships });
      const addSiteLocationAccessCategoryInput: AddSiteLocationAccessCategoryInput = {
        name: addAccessCategoryResponse.name.replace(/'/g, "''"),
        membershipIds: Array.isArray(data.memberships)
          ? data.memberships.map((id) => +id)
          : [data.memberships],
        siteLocationIds: Array.isArray(data.gyms) ? data.gyms.map((id) => +id) : [data.gyms],
        gender: addAccessCategoryResponse.gender,
        age: addAccessCategoryResponse.age,
        accessCategoryId: addAccessCategoryResponse.id,
        arxAccessCategoryId: data.groupId,
        status: addAccessCategoryResponse.status,
        createdBy: userId,
        lastModifiedBy: userId,
        schedulesOnly: schedulesOnly,
        siteLocationMemberships: []
      };

      console.log({ addSiteLocationAccessCategoryInput });

      const result = await (API.graphql({
        query: addAccessCategory,
        variables: {
          input: addSiteLocationAccessCategoryInput
        }
      }) as Promise<{
        data: { addAccessCategory: LambdaResponse };
      }>);
      console.log(result);
      if (result.data.addAccessCategory?.error) {
        throw result.data.addAccessCategory;
      }
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addSiteAccessCategory = async (data: FormInputs) => {
    console.log('addAccessCategory:', data);
    const addAccessCategoryInput: GymChainAccessCategoryInput = {
      siteId: +chainId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: +data.age || 0,
      schedulesOnly: schedulesOnly,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };
    console.log('addAccessCategory data.gyms', data.gyms);
    console.log({ addAccessCategoryInput });

    try {
      const addAccessCategoryResponse = await (API.graphql({
        query: addGymChainAccessCategory,
        variables: {
          input: addAccessCategoryInput
        }
      }) as Promise<{
        data: { addGymChainAccessCategory: GymChainAccessCategory };
      }>);
      console.log({ addAccessCategoryResponse });

      if (data.memberships.length > 0) {
        await addSiteLocationAccessCategory(
          data,
          addAccessCategoryResponse.data.addGymChainAccessCategory
        );
      }
      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`), 3000);
    } catch (e) {
      setFailedDialogVisible(true);
      console.log(e);
    }
  };

  const modifyAccessCategory = async (data: FormInputs) => {
    setIsInserted(true);
    try {
      const modifyInput: GymChainAccessCategoryInput = {
        id: props.accessCategory.id,
        siteId: props.accessCategory.siteId || 0,
        name: data.name || '',
        gender: data.gender || '',
        age: data.age || 0,
        schedulesOnly: schedulesOnly,
        status: data.status || '',
        lastModifiedBy: userId,
        createdBy: userId
      };

      const { data: modifyResponse } = (await API.graphql({
        query: modifyGymChainAccessCategory,
        variables: { input: modifyInput }
      })) as { data: { modifyGymChainAccessCategory: GymChainAccessCategory } };

      const accessCategoryData = modifyResponse.modifyGymChainAccessCategory;

      const currentGymIds = defaultSelectedGyms.map((y) => y.siteLocationId);
      const currentMembershipIds = defaultSelectedMemberships.map((membership) =>
        Number(membership.membershipId)
      );
      const addedGyms = data.gyms?.filter((id) => !currentGymIds.includes(+id)) || [];
      const addedMemberships =
        data.memberships?.filter((id) => !currentMembershipIds.includes(+id)) || [];

      const tasks: Promise<any>[] = [];

      if (addedGyms.length > 0 || addedMemberships.length > 0) {
        tasks.push(addSiteLocationAccessCategory(data, accessCategoryData));
      }

      if (data.groupId !== defaultValues.groupId) {
        console.log(data.gyms);
        console.log(data.gyms.map((id) => +id));
        tasks.push(
          modifySiteLocationAccessCategory({
            siteLocationIds: `(${data.gyms.map((id) => +id).join(', ')})`,
            membershipIds: `(${data.memberships.join(', ')})`,
            arxAccessCategoryId: data.groupId,
            accessCategoryId: accessCategoryData.id,
            lastModifiedBy: userId
          })
        );
      }
      console.log('here');

      const removedGyms = currentGymIds.filter((id) => !data.gyms?.includes(String(id)));

      removedGyms.forEach((gymId) => {
        defaultSelectedMemberships.forEach((membership) => {
          const linkedLoc = membership.siteLocations.find((loc) => loc.siteLocationId === gymId);

          if (linkedLoc?.siteLocationAccessCategoryId) {
            tasks.push(removeLocationAccessCategory(linkedLoc.siteLocationAccessCategoryId));
          }
        });
      });

      const removedMemberships = currentMembershipIds.filter(
        (id) => !data.memberships?.includes(id)
      );

      removedMemberships.forEach((id) => {
        const membershipEntry = defaultSelectedMemberships.find((m) => m.membershipId === id);
        membershipEntry?.siteLocations.forEach((loc) => {
          if (loc.siteLocationAccessCategoryId) {
            tasks.push(removeLocationAccessCategory(loc.siteLocationAccessCategoryId));
          }
        });
      });

      await Promise.all(tasks);

      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });

      setTimeout(() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`), 2000);
    } catch (error) {
      setFailedDialogVisible(true);
      console.error('Modification failed:', error);
    } finally {
      setIsInserted(false);
    }
  };

  const onSubmitHandler = async (data: FormInputs) => {
    setIsInserted(true);
    if (props.type === 'new') {
      await addSiteAccessCategory(data);
    } else if (props.type === 'modify' && props.accessCategory) {
      await modifyAccessCategory(data);
    }
    setIsInserted(false);
  };

  useEffect(() => {
    register('name', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string,
      maxLength: { value: 255, message: 'required' as string }
    });
    register('gender');
    register('age');
    register('schedulesOnly');
    register('status', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('gyms');
    register('memberships');
    register('groupId');
  }, [register]);

  const dropdownChangeHandler = (e: DropdownChangeEvent) => {
    setValue(e.target.name as keyof GymChainAccessCategoryInput, e.value);
    setFormValues({ ...formValues, [e.target.name]: e.value });
  };
  const statusDropDownItems = [
    {
      label: t(getTranslationKey('FORM_STATUS_DROPDOWN_ACTIVE')),
      value: 'active'
    },
    { label: t(getTranslationKey('FORM_STATUS_DROPDOWN_INACTIVE')), value: 'inactive' }
  ];
  const statusDropdownHandler = (e: DropdownChangeEvent) => {
    setValue('status', e.value);
    setFormValues({ ...formValues, status: e.value });
  };

  const changeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof FormInputs, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
    console.log({ formValues });
  };
  const loaderElement = () => {
    return (
      <div className="loaderHeader">
        <div className="loaderContent">
          <Loader shape="rectangle" width="quarter" />
        </div>
        <div>
          <Loader shape="rectangle" width="full" />
        </div>
      </div>
    );
  };
  return (
    <div>
      {isAccessCategoriesLoading ? (
        [...Array(5)].map((element, index) => {
          return <div key={index}> {loaderElement()} </div>;
        })
      ) : (
        <form className="input-mb20" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
          <div className="col-12 mb25">
            <Input
              label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_LABEL'))}
              variant="basic"
              name="name"
              value={formValues.name}
              onChange={inputFieldChangeHandler}
              error={errors.name ? true : false}
              errorMessage={errors.name?.message}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_PLACEHOLDER'))}
              required={true}
            />
          </div>

          <div className="col-12 mb25">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_LABEL'))}
            </label>
            <Dropdown
              key={'gender-selection'}
              name="gender"
              options={genderDropDownItems}
              value={formValues.gender}
              onChange={dropdownChangeHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_PLACEHOLDER'))}
              error={errors.gender ? true : false}
            />
          </div>
          <div className="col-12 mb25">
            <Input
              label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_LABEL'))}
              variant="basic"
              name="age"
              value={formValues.age > 0 ? formValues.age.toString() : ''}
              onChange={inputFieldChangeHandler}
              error={errors.age ? true : false}
              errorMessage={errors.age?.message}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_PLACEHOLDER'))}
              required={false}
            />
          </div>

          <div className="col-12 mb25">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_LABEL'))}
              <span className="p-error"> *</span>
            </label>
            <Dropdown
              key={'status-selection'}
              name="status"
              options={statusDropDownItems}
              value={formValues.status}
              onChange={statusDropdownHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_PLACEHOLDER'))}
              error={errors.status ? true : false}
            />
          </div>
          <div className="col-12 mb25">
            <label className="fw-bold">{t(getTranslationKey('FORM_INPUT_LABEL_GYMS'))}</label>
            {gymsDropDownItems.length > 0 ? (
              <Dropdown
                key={`gyms-count-${gymsDropDownItems.length}`}
                chips={true}
                name="gyms"
                placeholder={t(getTranslationKey('FORM_INPUT_PLACEHOLDER_GYMS'))}
                value={formValues.gyms || []}
                options={gymsDropDownItems}
                onChange={gymsDropdownHandler}
                error={errors.gyms ? true : false}
              />
            ) : (
              <div className="p-inputtext p-disabled">{t(getTranslationKey('LOADING_GYMS'))}</div>
            )}
            {errors.gyms && (
              <p data-cy={'membership-info-temp-form-dropdown-gyms-error'} className="p-error">
                {t(getTranslationKey('FORM_GYMS_EMPTY_ERROR'))}
              </p>
            )}
          </div>
          <div className="col-12 mb25">
            <h4>Selected Locations:</h4>
            {formValues.gyms?.map((id) => {
              const matchingItem = gymsDropDownItems.find((obj) => obj.value === id);

              return (
                <span
                  key={id}
                  className="p-badge"
                  style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                  {matchingItem ? matchingItem.label : t('COMMON.LOADING')}
                </span>
              );
            })}
          </div>

          <div className="col-12 mb25">
            <Checkbox
              name="filterMemberships"
              label={t(getTranslationKey('FILTER_MEMBERSHIPS_CHECKBOX'))}
              checked={filterMemberships}
              onChange={() => {
                setFilterMemberships(!filterMemberships);
              }}
            />
          </div>

          <div className="col-12 mb25">
            <label className="fw-bold">{t(getTranslationKey('FORM_INPUT_LABEL_MEMBERSHIP'))}</label>
            <Dropdown
              loading={isMembershipLoading}
              placeholder={
                isMembershipLoading
                  ? t(getTranslationKey('LOADING_MEMBERSHIPS'))
                  : t(getTranslationKey('FORM_INPUT_PLACEHOLDER_MEMBERSHIP'))
              }
              options={membershipsDropDownItems}
              disabled={isMembershipLoading || formValues.gyms.length === 0}
              key={`membership-dropdown-${membershipsDropDownItems.length}-${filterMemberships}`}
              chips={true}
              name="memberships"
              value={formValues.memberships || []}
              onChange={membershipsDropdownHandler}
              error={errors.memberships ? true : false}
            />

            {errors.memberships && (
              <p
                data-cy={'membership-info-temp-form-dropdown-memberships-error'}
                className="p-error">
                {t(getTranslationKey('FORM_MEMBERSHIPS_EMPTY_ERROR'))}
              </p>
            )}
          </div>

          <div className="col-12 mb25">
            <h4>Selected Memberships:</h4>
            {!isMembershipLoading &&
              formValues.memberships?.map((id) => {
                const matchingItem = membershipsDropDownItems.find(
                  (obj) => String(obj.value) === String(id)
                );

                return (
                  <span
                    key={id}
                    className="p-badge"
                    style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                    {matchingItem ? matchingItem.label : t('COMMON.LOADING')}
                  </span>
                );
              })}
          </div>
          <div className="row">
            <div className="col-12 mb25">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GROUP_ID_LABEL'))}
                variant="basic"
                name="groupId"
                value={formValues.groupId}
                onChange={inputFieldChangeHandler}
                error={errors.groupId ? true : false}
                errorMessage={errors.age?.message}
                placeholder={t(
                  getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GROUP_ID_PLACEHOLDER')
                )}
                required={false}
              />
            </div>
          </div>

          <div className="d-flex flex-row mt35 btn-min-w-110">
            <div className="mr15">
              <Button
                data-cy={'membership-info-temp-form-btn-save'}
                icon={isInserted ? 'pi-spinner pi-spin' : ''}
                label={t(getTranslationKey('FORM_BTN_LABEL_SAVE'))}
                size={ButtonSizes.medium}
                disabled={isInserted}
              />
            </div>
            <Button
              data-cy={'membership-info-temp-form-btn-cancel'}
              label={t(getTranslationKey('FORM_BTN_LABEL_CANCEL'))}
              variant={ButtonVariants.textonly}
              onClick={() => history.push(`/gymChains/${chainId}/gymChainAccessCategories`)}
              disabled={isInserted}
            />
            <ActionFailedDialog />
            <RemovableGymDialog />
          </div>
        </form>
      )}
    </div>
  );
};

export default GymChainAccessCategoryInfoTemplate;

type GymChainAccessCategoryInfoTemplateType = 'new' | 'modify';

export interface GymChainAccessCategoryInfoTemplateProps {
  type: GymChainAccessCategoryInfoTemplateType;
  accessCategory?: GymChainAccessCategory;
  alertRef: RefObject<AlertType>;
  alertMessage: string;
}
